import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import AiInsightPreview from "./AiInsightPreview";
import AiRadarCard from "./AiRadarCard";
import DashboardBottomSection from "./DashboardBottomSection";
import BusinessNamePrompt from "./BusinessNamePrompt";
import RankingEngine from "./RankingEngine";
import GoogleAdsTab from "./GoogleAdsTab";
import PerformancePanel from "./PerformancePanel";
import PulseLineAnimation from "./PulseLineAnimation";
import GscPremiumCard from "./GscPremiumCard";
import { startGscOAuth as startGscOAuthExternal } from "@/lib/gscOAuth";
import { normalizeKeyword } from "@/lib/keywordGroups";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";


interface Props {
  clientIds: string[];
  businessName: string | null;
  /** Dedicated human-readable entity name from clients.business_name. */
  businessNameField?: string | null;
}

const DashboardHome = ({ clientIds, businessName, businessNameField }: Props) => {
  const navigate = useNavigate();
  const [knownFor, setKnownFor] = useState<string | null>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [ranks, setRanks] = useState<any[]>([]);
  const [speed, setSpeed] = useState<any[]>([]);
  const [backlinks, setBacklinks] = useState<any[]>([]);
  const [trackedKeywords, setTrackedKeywords] = useState<any[]>([]);
  const [keywordGroups, setKeywordGroups] = useState<any[]>([]);
  const [conductorLogs, setConductorLogs] = useState<any[]>([]);
  const [ownClientId, setOwnClientId] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null);
  const [gscToken, setGscToken] = useState<string | null>(null);
  const [gscLoading, setGscLoading] = useState(false);
  const [gscError, setGscError] = useState<string | null>(null);
  const [gscData, setGscData] = useState<{ clicks: number; impressions: number; ctr: number; position: number; siteUrl?: string; dateRange?: string; startDate?: string; endDate?: string; daily?: Array<{ date: string; clicks: number; impressions: number; ctr: number; position: number }> } | null>(null);
  const [gscProperty, setGscProperty] = useState<string | null>(null);
  const [gscPropertySelected, setGscPropertySelected] = useState<boolean>(false);
  const [gscConnectedAt, setGscConnectedAt] = useState<string | null>(null);
  const [gscReconnectDismissed, setGscReconnectDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem("gsc_reconnect_dismissed") === "1"; } catch { return false; }
  });
  const [gscDateRange, setGscDateRange] = useState<"24h" | "7d" | "28d" | "90d">("28d");
  const [seoLabelUrl, setSeoLabelUrl] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // SEO Engine label — always fresh fetch scoped to the current logged-in user.
  // Independent of clientIds / props / any cached value.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { isDemoMode, getDemoClientId } = await import("@/lib/demoMode");
      if (isDemoMode()) {
        const { data: client } = await supabase
          .from("clients").select("website_url").eq("id", getDemoClientId()!).maybeSingle();
        if (!cancelled) setSeoLabelUrl((client as any)?.website_url || null);
        return;
      }
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) return;
      const { data: client } = await supabase
        .from("clients")
        .select("user_id,website_url")
        .eq("user_id", uid)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const fresh = client && client.user_id === uid ? (client.website_url || null) : null;
      setSeoLabelUrl(fresh);
    })();
    return () => { cancelled = true; };
  }, []);

  // Always resolve the GSC-target client by the currently logged-in user.
  // In demo mode, returns the configured demo client without touching auth.
  const getOwnClient = async () => {
    const { isDemoMode, getDemoClientId } = await import("@/lib/demoMode");
    if (isDemoMode()) {
      const demoId = getDemoClientId();
      if (!demoId) return null;
      const { data } = await supabase
        .from("clients")
        .select("id,user_id,website_url,gsc_property,gsc_property_selected,gsc_available_properties,gsc_connected_at,known_for")
        .eq("id", demoId)
        .maybeSingle();
      return (data as any) || null;
    }
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    if (!uid) return null;
    const { data } = await supabase
      .from("clients")
      .select("id,user_id,website_url,gsc_property,gsc_property_selected,gsc_available_properties,gsc_connected_at,known_for")
      .eq("user_id", uid)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (!data || data.user_id !== uid) return null;
    return data as any;
  };

  // Only fetches GSC data for the explicitly selected property — never auto-picks.
  // In demo mode we ALWAYS return a stable seeded snapshot, never hit the edge function.
  const fetchGscData = async (selectedProperty: string, range: "24h" | "7d" | "28d" | "90d" = gscDateRange) => {
    const { isDemoMode } = await import("@/lib/demoMode");
    if (isDemoMode()) {
      const { getDemoGscSnapshot } = await import("@/lib/demoGscData");
      setGscError(null);
      setGscLoading(true);
      // tiny delay so the UI feels live, not instant-flash
      await new Promise((r) => setTimeout(r, 180));
      setGscData(getDemoGscSnapshot(range) as any);
      setGscLoading(false);
      return;
    }
    setGscLoading(true);
    setGscError(null);
    const client = await getOwnClient();
    console.log("[GSC] fetching with property:", selectedProperty, "range:", range, "clientId:", client?.id);
    const { data, error } = await supabase.functions.invoke("get-gsc-data", {
      body: { gscProperty: selectedProperty, dateRange: range, clientId: client?.id },
    });
    setGscLoading(false);
    const payload: any = data;
    if (error || payload?.ok === false || payload?.error) {
      setGscError(payload?.error || error?.message || "Failed to fetch GSC data");
      return;
    }
    setGscData(payload);
  };

  const handleDateRangeChange = (range: "24h" | "7d" | "28d" | "90d") => {
    if (range === gscDateRange) return;
    setGscDateRange(range);
    if (gscProperty && gscPropertySelected) {
      fetchGscData(gscProperty, range);
    }
  };

  const startGscOAuth = async () => {
    setGscError(null);
    const { isDemoMode } = await import("@/lib/demoMode");
    if (isDemoMode()) {
      // Demo mode: never trigger OAuth — the demo is permanently "connected".
      // Just refresh the seeded snapshot so the UI feels responsive.
      if (gscProperty) fetchGscData(gscProperty, gscDateRange);
      return;
    }
    try {
      // Dedicated Google OAuth flow that does NOT touch supabase.auth — preserves the user session.
      await startGscOAuthExternal();
    } catch (err: any) {
      setGscError(err?.message || "Could not start Google Search Console connection.");
    }
  };

  const handleConnectGsc = () => startGscOAuth();

  const wipeGscClientRecord = async (own: any) => {
    const { data: beforeWipe } = await supabase
      .from("clients")
      .select("id,gsc_property,gsc_property_selected,gsc_available_properties,gsc_connected_at")
      .eq("id", own.id)
      .eq("user_id", own.user_id)
      .maybeSingle();
    console.log("[GSC] client row before wipe:", beforeWipe);

    const { error: wipeError } = await supabase
      .from("clients")
      .update({
        gsc_access_token: null,
        gsc_refresh_token: null,
        gsc_property: null,
        gsc_property_selected: false,
        gsc_available_properties: null,
        gsc_connected_at: null,
      } as any)
      .eq("id", own.id)
      .eq("user_id", own.user_id);

    if (wipeError) throw wipeError;

    const { data: afterWipe } = await supabase
      .from("clients")
      .select("id,gsc_property,gsc_property_selected,gsc_available_properties,gsc_connected_at")
      .eq("id", own.id)
      .eq("user_id", own.user_id)
      .maybeSingle();
    console.log("[GSC] client row after wipe:", afterWipe);

    setGscToken(null);
    setGscProperty(null);
    setGscPropertySelected(false);
    setGscData(null);
  };

  const handleChangeProperty = async () => {
    setGscError(null);
    const { isDemoMode } = await import("@/lib/demoMode");
    if (isDemoMode()) {
      // Demo: ignore — Sandton Locksmith property is locked.
      return;
    }
    try {
      const own = await getOwnClient();
      if (!own?.id) throw new Error("Could not find your client record.");
      // Only clear the selected property — keep tokens so we can re-pick without re-OAuth.
      // We then redirect to the dedicated GSC OAuth flow (no supabase.auth.signOut, no signInWithOAuth).
      await supabase
        .from("clients")
        .update({ gsc_property: null, gsc_property_selected: false } as any)
        .eq("id", own.id)
        .eq("user_id", own.user_id);
      setGscProperty(null);
      setGscPropertySelected(false);
      setGscData(null);
      await startGscOAuthExternal();
    } catch (err: any) {
      setGscError(err?.message || "Could not restart Google Search Console connection.");
    }
  };

  const handleDisconnectGsc = async () => {
    setGscError(null);
    const { isDemoMode } = await import("@/lib/demoMode");
    if (isDemoMode()) {
      // Demo: GSC stays permanently connected.
      return;
    }
    try {
      const own = await getOwnClient();
      if (!own?.id) throw new Error("Could not find your client record.");
      await wipeGscClientRecord(own);
    } catch (err: any) {
      setGscError(err?.message || "Could not disconnect Google Search Console.");
    }
  };

  const loadLiveTasks = async (clientId: string) => {
    const { data } = await supabase
      .from("conductor_logs")
      .select("id,type,message,status,attempt,max_attempts,created_at")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false })
      .limit(12);
    setConductorLogs(data || []);
  };

  // Initial data load — strictly scoped to the currently logged-in user's own client.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ownClient = await getOwnClient();
      if (cancelled) return;
      if (!ownClient?.id) {
        console.log("[KEYWORDS] no own client found for current user");
        setOwnClientId(null);
        return;
      }
      const oid = ownClient.id as string;
      setOwnClientId(oid);
      console.log("[KEYWORDS] filtering by client_id:", oid);

      const [p, r, s, b, k, cl, kg] = await Promise.all([
        supabase.from("blog_posts").select("id,created_at,indexed_at,title,keyword").eq("client_id", oid),
        supabase.from("rank_tracking").select("*").eq("client_id", oid).order("checked_at", { ascending: true }),
        supabase.from("page_speed").select("*").eq("client_id", oid).order("checked_at", { ascending: false }).limit(1),
        supabase.from("backlinks").select("*").eq("client_id", oid),
        supabase.from("keywords").select("id,keyword,created_at,gsc_impressions").eq("client_id", oid).order("created_at", { ascending: true }),
        supabase.from("conductor_logs").select("id,type,message,status,attempt,max_attempts,created_at").eq("client_id", oid).order("created_at", { ascending: false }).limit(12),
        supabase.from("keyword_groups").select("id,canonical_keyword,normalized_key,total_impressions,best_position,status,variants").eq("client_id", oid),
      ]);
      if (cancelled) return;
      setPosts(p.data || []);
      setRanks(r.data || []);
      setSpeed(s.data || []);
      setBacklinks(b.data || []);
      setTrackedKeywords(k.data || []);
      setConductorLogs(cl.data || []);
      setKeywordGroups(kg.data || []);

      const client: any = ownClient;
      setWebsiteUrl(client?.website_url || null);

      const { isDemoMode } = await import("@/lib/demoMode");
      if (isDemoMode()) {
        // Demo: force a permanent "connected" GSC state and load the seeded snapshot.
        const { DEMO_GSC_PROPERTY } = await import("@/lib/demoGscData");
        setGscToken("demo");
        setGscProperty(DEMO_GSC_PROPERTY);
        setGscPropertySelected(true);
        setGscConnectedAt(
          client?.gsc_connected_at || new Date(Date.now() - 6 * 60 * 1000).toISOString()
        );
        fetchGscData(DEMO_GSC_PROPERTY, gscDateRange);
      } else {
        // Tokens are not readable from the browser; use the connection timestamp.
        const isConnected = !!client?.gsc_connected_at;
        const savedProperty = client?.gsc_property || null;
        const selected = !!client?.gsc_property_selected;
        setGscToken(isConnected ? "connected" : null);
        setGscProperty(savedProperty);
        setGscPropertySelected(selected);
        setGscConnectedAt(client?.gsc_connected_at || null);
        if (isConnected && selected && savedProperty) {
          fetchGscData(savedProperty);
        }
      }
      setKnownFor(client?.known_for || null);
    })();
    return () => { cancelled = true; };
  }, [clientIds]);

  useEffect(() => {
    if (!ownClientId) return;
    let stopped = false;
    const refreshAndRetry = async () => {
      if (stopped) return;
      await loadLiveTasks(ownClientId);
      const { isDemoMode } = await import("@/lib/demoMode");
      if (isDemoMode()) return;
      await supabase.functions.invoke("retry-failed-articles", { body: { client_id: ownClientId } });
    };
    refreshAndRetry();
    const interval = window.setInterval(refreshAndRetry, 4000);
    return () => { stopped = true; window.clearInterval(interval); };
  }, [ownClientId]);

  // GSC connect/reconnect is now handled at /gsc-callback by exchange-gsc-code edge function.
  // We only handle the legacy ?gsc=reconnect query param here by kicking off the new OAuth flow.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("gsc");
    if (flag === "reconnect") {
      window.history.replaceState({}, "", window.location.pathname);
      startGscOAuth();
    } else if (flag === "connect") {
      // Legacy callback — should no longer happen. Just clean the URL.
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const totalPages = posts.length;
  const bestRank = ranks.length ? Math.min(...ranks.filter(r => r.rank).map(r => r.rank)) : null;
  const healthScore = speed.length ? Math.round(((speed[0].performance || 0) + (speed[0].seo || 0) + (speed[0].accessibility || 0) + (speed[0].best_practices || 0)) / 4) : null;

  const rankChartData = (() => {
    if (!ranks.length) return [];
    const weeks: Record<string, number[]> = {};
    ranks.forEach(r => {
      if (!r.rank) return;
      const d = new Date(r.checked_at);
      const wk = `${d.getMonth() + 1}/${d.getDate()}`;
      if (!weeks[wk]) weeks[wk] = [];
      weeks[wk].push(r.rank);
    });
    return Object.entries(weeks).map(([w, vals]) => ({
      week: w,
      rank: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length),
    }));
  })();

  // Build normalized-key → max GSC impressions lookup (across all variants).
  const impressionsByKey = (() => {
    const m: Record<string, number> = {};
    (trackedKeywords as any[]).forEach((k) => {
      const key = normalizeKeyword(String(k.keyword || ""));
      if (!key) return;
      const imp = typeof k.gsc_impressions === "number" ? k.gsc_impressions : 0;
      if (!(key in m) || imp > m[key]) m[key] = imp;
    });
    return m;
  })();

  // Latest + previous rank per NORMALIZED key from rank_tracking.
  // GATE: only keep entries that are GSC-confirmed (source='gsc' OR any variant has impressions>0).
  const latestRankByKey = (() => {
    const map: Record<string, { rank: number | null; checked_at: string; previousRank: number | null }> = {};
    ranks.forEach((r) => {
      if (!r.keyword) return;
      const key = normalizeKeyword(String(r.keyword));
      if (!key) return;
      const src = String(r.source || "").toLowerCase();
      const imp = impressionsByKey[key] ?? 0;
      const gscConfirmed = src === "gsc" || imp > 0;
      if (!gscConfirmed) return;
      const prev = map[key];
      if (!prev) {
        map[key] = { rank: r.rank ?? null, checked_at: r.checked_at, previousRank: null };
      } else {
        map[key] = { rank: r.rank ?? null, checked_at: r.checked_at, previousRank: prev.rank };
      }
    });
    return map;
  })();

  // Ranking Progress list — one row PER GROUP (canonical_keyword).
  // Rank source priority: rank_tracking GSC entry → group.best_position fallback.
  const rankingSetupKeywords = (() => {
    const enriched = (keywordGroups as any[]).map((g) => {
      const key: string = g.normalized_key;
      const variants: string[] = Array.isArray(g.variants) ? g.variants : [];
      const latest = latestRankByKey[key];
      let rank: number | null = null;
      let previousRank: number | null = null;
      if (latest && latest.rank != null && latest.rank <= 100) {
        rank = latest.rank;
        previousRank = latest.previousRank;
      } else if (g.best_position != null) {
        rank = Math.round(Number(g.best_position));
      }
      // Honesty rule: trend deltas based on <5 GSC impressions are noise, not signal.
      const totalImp = Number(g.total_impressions ?? 0) || 0;
      const hasEnoughSignal = totalImp >= 5;
      const delta =
        hasEnoughSignal && rank != null && previousRank != null
          ? previousRank - rank
          : null;
      let tier = 0;
      if (rank != null) {
        if (rank <= 3) tier = 1;
        else if (rank <= 10) tier = 2;
        else if (rank <= 20) tier = 3;
        else tier = 4;
      }
      const pending = rank == null;
      return {
        id: g.id,
        keyword: g.canonical_keyword as string,
        variants,
        rank,
        previousRank,
        delta,
        tier,
        pending,
      };
    });
    enriched.sort((a, b) => {
      if (a.pending && !b.pending) return 1;
      if (!a.pending && b.pending) return -1;
      if (a.rank == null && b.rank == null) return 0;
      if (a.rank == null) return 1;
      if (b.rank == null) return -1;
      return a.rank - b.rank;
    });
    return enriched;
  })();

  const growthData = (() => {
    if (!posts.length) return [];
    const weeks: Record<string, number> = {};
    posts.forEach(p => {
      const d = new Date(p.created_at);
      const wk = `W${Math.ceil(d.getDate() / 7)}`;
      weeks[wk] = (weeks[wk] || 0) + 1;
    });
    return Object.entries(weeks).map(([w, c]) => ({ week: w, pages: c }));
  })();

  const topKeywords = rankingSetupKeywords
    .filter((k) => !k.pending && k.rank != null)
    .map((k) => ({ keyword: k.keyword, rank: k.rank as number }))
    .sort((a, b) => a.rank - b.rank)
    .slice(0, 5);

  const formatTimeAgo = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hr ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // Real Live Tasks from conductor_logs (already scoped to ownClientId).
  const classifyLog = (type: string | null | undefined, message: string | null | undefined) => {
    const v = `${type || ""} ${message || ""}`.toLowerCase();
    // category
    let category: "article" | "translation" | "indexing" | "keyword" | "rank" | "backlink" | "speed" | "ai" | "other" = "other";
    let icon = "•";
    let priority = 0; // higher = more important
    if (/(translat)/.test(v)) { category = "translation"; icon = "🌐"; priority = 4; }
    else if (/(article|post|blog|content|generat)/.test(v)) { category = "article"; icon = "📝"; priority = 5; }
    else if (/(index|sitemap|crawl|publish)/.test(v)) { category = "indexing"; icon = "🗂️"; priority = 4; }
    else if (/(keyword)/.test(v)) { category = "keyword"; icon = "🔑"; priority = 2; }
    else if (/(rank)/.test(v)) { category = "rank"; icon = "📈"; priority = 3; }
    else if (/(backlink|citation|director)/.test(v)) { category = "backlink"; icon = "🔗"; priority = 3; }
    else if (/(speed|perf)/.test(v)) { category = "speed"; icon = "⚡"; priority = 2; }
    else if (/(ai|insight|positioning)/.test(v)) { category = "ai"; icon = "🧠"; priority = 3; }
    // status
    let status: "running" | "completed" | "failed" = "completed";
    if (/(fail|error|❌|could not)/.test(v)) status = "failed";
    else if (/(running|start|in[- ]?progress|generating|fetch|analy|process|scan)/.test(v)) status = "running";
    else if (/(done|complete|success|finished|published|created|added|✅)/.test(v)) status = "completed";
    return { category, icon, priority, status };
  };
  // Turn raw conductor log strings into a clean, human-readable status sentence.
  // Strips brackets/pipes/key=val syntax and produces patterns like:
  //   "Optimized content for 24 hrs locksmith"
  //   "Discovery: High-intent opportunity for 24 hrs locksmith"
  //   "Market Targeting: French (FR)"
  const LANG_NAMES: Record<string, string> = {
    en: "English", fr: "French", es: "Spanish", de: "German", it: "Italian",
    pt: "Portuguese", nl: "Dutch", pl: "Polish", ru: "Russian", ja: "Japanese",
    zh: "Chinese", ko: "Korean", ar: "Arabic", tr: "Turkish", sv: "Swedish",
    da: "Danish", no: "Norwegian", fi: "Finnish", cs: "Czech", el: "Greek",
  };
  const humanizeTaskMessage = (
    rawType: string | null | undefined,
    rawMessage: string | null | undefined,
    category: string,
  ): { text: string; keyword: string | null } => {
    const original = (rawMessage || rawType || "Task").trim();
    let s = original;

    // 1. Pull out the keyword from common patterns (keyword="x", keyword=x, [x], "x").
    let keyword: string | null = null;
    const kwQuoted = s.match(/keyword\s*[:=]\s*["“']([^"”']+)["”']/i);
    const kwBare = s.match(/keyword\s*[:=]\s*([^\|,\];]+)/i);
    const bracketed = s.match(/\[([^\]]+)\]/);
    const quoted = s.match(/["“']([^"”']{2,})["”']/);
    if (kwQuoted) keyword = kwQuoted[1].trim();
    else if (kwBare) keyword = kwBare[1].trim();
    else if (quoted) keyword = quoted[1].trim();
    else if (bracketed && !/^[a-z]{2}$/i.test(bracketed[1].trim())) keyword = bracketed[1].trim();

    // 2. Pull out a language code like [fr] or lang=fr.
    let lang: string | null = null;
    const langTag = s.match(/\b(?:lang(?:uage)?\s*[:=]\s*|\[)([a-z]{2})\b\]?/i);
    if (langTag) lang = langTag[1].toLowerCase();

    // 3. Strip syntax noise: pipes, brackets, key=val pairs, stray quotes.
    s = s
      .replace(/\bkeyword\s*[:=]\s*["“']?[^"”'\|,\];]+["”']?/gi, "")
      .replace(/\blang(?:uage)?\s*[:=]\s*[a-z\-]+/gi, "")
      .replace(/\[[^\]]*\]/g, "")
      .replace(/[|]+/g, " ")
      .replace(/["“”']/g, "")
      .replace(/\s{2,}/g, " ")
      .replace(/^\W+|\W+$/g, "")
      .trim();

    const lower = `${rawType || ""} ${original}`.toLowerCase();

    // 4. Compose a natural sentence based on category + verbs detected.
    let text = "";
    if (category === "translation" && lang) {
      const langName = LANG_NAMES[lang] || lang.toUpperCase();
      text = keyword
        ? `Market Targeting: ${langName} — ${keyword}`
        : `Market Targeting: ${langName}`;
    } else if (/optimi[sz]/.test(lower)) {
      text = keyword ? `Optimized content for ${keyword}` : "Optimized content";
    } else if (/(generat|creat).*image|photo|imagery/.test(lower)) {
      text = keyword
        ? `Generated professional editorial photo for ${keyword}`
        : "Generated professional editorial photo";
    } else if (/publish/.test(lower)) {
      text = keyword ? `Published new article for ${keyword}` : "Published new article";
    } else if (category === "article" && /(generat|creat|writ)/.test(lower)) {
      text = keyword ? `Writing article for ${keyword}` : "Writing new article";
    } else if (category === "indexing") {
      const mGoogle = lower.match(/submitted\s+(\d+)\s+pages?\s+to\s+google\s+indexing\s+api\s*\+\s*bing\s+indexnow/);
      const mQuota = lower.match(/submitted\s+(\d+)\s+pages?\s+to\s+bing\s+indexnow.*google\s+quota/);
      const mBing = lower.match(/submitted\s+(\d+)\s+pages?\s+to\s+bing\s+indexnow/);
      if (mGoogle) {
        const n = parseInt(mGoogle[1], 10);
        text = `Submitted ${n} page${n === 1 ? "" : "s"} to Google Indexing API + Bing IndexNow`;
      } else if (mQuota) {
        const n = parseInt(mQuota[1], 10);
        text = `Submitted ${n} page${n === 1 ? "" : "s"} to Bing IndexNow · Google quota reached for today`;
      } else if (mBing) {
        const n = parseInt(mBing[1], 10);
        text = `Submitted ${n} page${n === 1 ? "" : "s"} to Bing IndexNow`;
      } else if (/google quota/.test(lower)) {
        text = "Google Indexing API: daily quota reached — resumes tomorrow";
      } else if (/google indexing api/.test(lower)) {
        text = keyword ? `Submitted ${keyword} to Google Indexing API` : "Submitted to Google Indexing API";
      } else {
        text = keyword ? `Submitted ${keyword} to Bing IndexNow` : "Submitted new content to Bing IndexNow";
      }
    } else if (category === "keyword" && /(discover|found|new|opportunit)/.test(lower)) {
      text = keyword
        ? `Discovery: High-intent opportunity for ${keyword}`
        : "Discovery: New keyword opportunity";
    } else if (category === "keyword") {
      text = keyword ? `Keyword Matched: ${keyword}` : "Keyword research";
    } else if (category === "rank") {
      text = keyword ? `Tracking ranking for ${keyword}` : "Tracking keyword rankings";
    } else if (category === "backlink") {
      text = keyword ? `Building citation for ${keyword}` : "Building new citation";
    } else if (category === "speed") {
      text = "Analyzing site speed & Core Web Vitals";
    } else if (category === "ai") {
      text = keyword ? `Analyzing: ${keyword}` : "Generating AI insight";
    } else if (/analy|scan|check/.test(lower)) {
      text = keyword ? `Analyzing: ${keyword}` : "Analyzing website";
    } else {
      // Fallback: cleaned-up sentence, capitalized.
      text = s ? s.charAt(0).toUpperCase() + s.slice(1) : "Working on your SEO";
    }

    return { text, keyword };
  };

  const liveTasks = conductorLogs.map((l) => {
    const meta = classifyLog(l.type, l.message);
    const human = humanizeTaskMessage(l.type, l.message, meta.category);
    // Prefer the real persisted status (set by edge functions + retry worker).
    // Fall back to text-based classification for legacy logs without a status.
    const realStatus = (l.status as string | null | undefined) || null;
    const status: "running" | "completed" | "failed" | "recovering" =
      realStatus === "recovering" ? "recovering"
      : realStatus === "running" ? "running"
      : realStatus === "completed" || realStatus === "recovered" ? "completed"
      : realStatus === "failed" ? "failed"
      : meta.status;
    return {
      id: l.id,
      icon: meta.icon,
      category: meta.category,
      priority: meta.priority,
      status,
      attempt: l.attempt ?? 0,
      maxAttempts: l.max_attempts ?? 3,
      name: human.text,
      keyword: human.keyword,
      time: formatTimeAgo(l.created_at),
      createdAt: l.created_at,
    };
  }).sort((a, b) => {
    // recovering + running first, then failed, then by priority desc, then recency
    const rank = (s: string) => (s === "recovering" ? 3 : s === "running" ? 2 : s === "failed" ? 1 : 0);
    const sA = rank(a.status); const sB = rank(b.status);
    if (sA !== sB) return sB - sA;
    if (a.priority !== b.priority) return b.priority - a.priority;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // Pages this week (last 7 days), scoped via posts which were already client-filtered.
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const pagesThisWeek = posts.filter((p) => new Date(p.created_at).getTime() >= sevenDaysAgo).length;

  // Real top-10 keyword count from rank_tracking (latest rank per keyword).
  const latestRankPerKeyword: Record<string, number> = {};
  ranks.forEach((r) => {
    if (!r.rank || !r.keyword) return;
    const prev = latestRankPerKeyword[r.keyword];
    if (prev === undefined) { latestRankPerKeyword[r.keyword] = r.rank; return; }
    // ranks already ordered ascending by checked_at — last write wins
    latestRankPerKeyword[r.keyword] = r.rank;
  });
  const top10Count = Object.values(latestRankPerKeyword).filter((rk) => rk <= 10).length;
  const top3Count = Object.values(latestRankPerKeyword).filter((rk) => rk <= 3).length;
  const top1120Count = Object.values(latestRankPerKeyword).filter((rk) => rk > 10 && rk <= 20).length;
  const totalRankedKeywords = Object.keys(latestRankPerKeyword).length;

  // Honest AI Decisions feed — real titles, real keywords, real directory names. No invented phrasing.
  const aiDecisions: { icon: string; text: string; time: string }[] = [
    ...posts
      .filter((p) => p.title)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 4)
      .map((p) => ({
        icon: "📄",
        text: p.keyword ? `${p.title} — ${p.keyword}` : p.title,
        time: formatTimeAgo(p.created_at),
      })),
    ...backlinks
      .filter((b) => b.submitted_at && b.directory_name)
      .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
      .slice(0, 2)
      .map((b) => ({
        icon: "🔗",
        text: b.directory_name,
        time: formatTimeAgo(b.submitted_at),
      })),
  ].slice(0, 4);

  const automations = [
    { icon: "🧠", name: "Smart Optimization", desc: "AI adjusts content daily" },
    { icon: "👁️", name: "Continuous Monitoring", desc: "Rankings checked daily" },
    { icon: "⚡", name: "Instant Adjustments", desc: "Real-time SEO updates" },
    { icon: "🔗", name: "Backlink Building", desc: `${backlinks.length} directories active` },
  ];

  const green = "hsl(var(--primary))";
  const greenDim = "hsl(var(--primary) / 0.10)";
  const greenSoft = "hsl(var(--primary) / 0.05)";
  const greenBorder = "hsl(var(--primary) / 0.18)";
  const textMuted = "hsl(var(--muted-foreground))";
  const textDim = "hsl(var(--muted-foreground) / 0.8)";
  const cardStyle = {
    background: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 16,
    padding: isMobile ? 16 : 22,
    boxShadow: "0 8px 28px hsl(var(--foreground) / 0.08)",
  };
  const gscIsConnected = Boolean(gscToken || gscProperty || gscPropertySelected);

  const [view, setView] = useState<"seo" | "ai" | "ads">("seo");
  const [tierFilter, setTierFilter] = useState<0 | 1 | 2 | 3 | 4>(0);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("orbit:dashboard-view", { detail: view })
    );
  }, [view]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === "seo" || detail === "ai" || detail === "ads") {
        setView(detail);
      }
    };
    window.addEventListener("orbit:set-dashboard-view", handler);
    return () => window.removeEventListener("orbit:set-dashboard-view", handler);
  }, []);

  const switcher = (
    <div className="relative -mt-3 mb-5 flex items-center justify-center gap-2 sm:-mt-5 sm:mb-7 sm:gap-3 px-0 sm:px-2">
      <button
        type="button"
        onClick={() => { window.dispatchEvent(new CustomEvent("orbit:navigate-tab", { detail: "workspace" })); }}
        className="absolute left-0 sm:left-2 top-1/2 -translate-y-1/2 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-[13px] font-[700] sm:font-[800] leading-none tracking-[-0.01em] transition-all duration-300 orbit-workspace-pulse"
        style={{
          background: "hsl(var(--card))",
          color: "hsl(var(--foreground))",
          borderColor: "hsl(var(--primary) / 0.45)",
          boxShadow: "inset 0 0 12px hsl(var(--primary) / 0.18), 0 0 0 1px hsl(var(--primary) / 0.25)",
        }}
        aria-label="Workspace"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--primary))" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
        <span className="truncate">Workspace</span>
      </button>
      <style>{`
        @keyframes orbitWorkspacePulse {
          0%, 100% { box-shadow: inset 0 0 10px hsl(var(--primary) / 0.15), 0 0 0 1px hsl(var(--primary) / 0.22); }
          50%      { box-shadow: inset 0 0 16px hsl(var(--primary) / 0.32), 0 0 0 1px hsl(var(--primary) / 0.40); }
        }
        .orbit-workspace-pulse { animation: orbitWorkspacePulse 2.8s ease-in-out infinite; }
      `}</style>
      <div
        className="flex w-full max-w-[460px] gap-1 sm:gap-2 rounded-full border p-1 sm:p-1.5"
        style={{
          background: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
          boxShadow: "0 4px 18px hsl(var(--foreground) / 0.06)",
        }}
      >
        <button
          type="button"
          data-tour-section="seo"
          onClick={() => setView("seo")}
          className="flex-1 min-w-0 flex items-center justify-center gap-1 whitespace-nowrap rounded-full px-1.5 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-[13px] font-[700] sm:font-[800] leading-tight tracking-[-0.01em] transition-all duration-300"
          style={
            view === "seo"
              ? {
                  background: "hsl(var(--primary))",
                  color: "hsl(var(--primary-foreground))",
                  boxShadow: "0 0 0 1px hsl(var(--primary) / 0.4)",
                }
              : {
                  background: "transparent",
                  color: "hsl(var(--muted-foreground))",
                }
          }
        >
          <span className="hidden sm:inline">🚀</span>
          <span className="truncate">SEO Engine</span>
        </button>
        <button
          type="button"
          data-tour-section="ai"
          onClick={() => setView("ai")}
          className="flex-1 min-w-0 flex items-center justify-center gap-1 whitespace-nowrap rounded-full px-1.5 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-[13px] font-[700] sm:font-[800] leading-none tracking-[-0.01em] transition-all duration-300"
          style={
            view === "ai"
              ? {
                  background: "hsl(270 90% 60%)",
                  color: "#ffffff",
                  boxShadow: "0 0 0 1px hsl(270 90% 60% / 0.45)",
                }
              : {
                  background: "transparent",
                  color: "hsl(var(--muted-foreground))",
                }
          }
        >
          <span className="hidden sm:inline">✨</span>
          <span className="truncate">AI Search</span>
        </button>
        <button
          type="button"
          data-tour-section="ads"
          onClick={() => setView("ads")}
          className="flex-1 min-w-0 flex items-center justify-center gap-1 whitespace-nowrap rounded-full px-1.5 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-[13px] font-[700] sm:font-[800] leading-none tracking-[-0.01em] transition-all duration-300"
          style={
            view === "ads"
              ? {
                  background: "linear-gradient(135deg, #4285F4, #FBBC04)",
                  color: "#ffffff",
                  boxShadow: "0 0 0 1px rgba(66,133,244,0.45)",
                }
              : {
                  background: "transparent",
                  color: "hsl(var(--muted-foreground))",
                }
          }
        >
          <span className="hidden sm:inline">📣</span>
          <span className="truncate pb-[2px]">Google Ads</span>
        </button>
      </div>
    </div>
  );

  if (view === "ads") {
    return (
      <div className="text-foreground animate-fade-in" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {switcher}
        <GoogleAdsTab />
      </div>
    );
  }

  if (view === "ai") {
    return (
      <div className="text-foreground animate-fade-in" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {switcher}
        <RankingEngine />
        <AiInsightPreview />
        <AiRadarCard />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div className="text-foreground animate-fade-in" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {switcher}
      {ownClientId && (
        <BusinessNamePrompt
          clientId={ownClientId}
          currentBusinessName={businessNameField ?? null}
          fallbackName={businessName}
          onSaved={() => window.location.reload()}
        />
      )}
      {/* Cockpit header — calm, unified SEO + AI status */}
      <div
        className={`glass glass-tinted-green relative mb-[14px] overflow-hidden rounded-[18px] ${isMobile ? "flex-col gap-2.5" : "items-center justify-between"} flex`}
        style={{
          padding: isMobile ? "17px 14px" : "28px 28px",
          minHeight: isMobile ? 88 : 102,
        }}
      >
        {/* subtle ambient tints */}
        <div
          className="pointer-events-none absolute -left-20 top-1/2 h-[240px] w-[240px] -translate-y-1/2 rounded-full"
          style={{ background: `radial-gradient(circle, ${green} 0%, transparent 70%)`, opacity: 0.07 }}
        />
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-[240px] w-[240px] rounded-full"
          style={{ background: "radial-gradient(circle, hsl(270 80% 60%) 0%, transparent 70%)", opacity: 0.06 }}
        />

        {/* LEFT — SEO Engine status (green) */}
        <div className="relative z-10 flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span
              className="absolute inline-flex h-full w-full rounded-full soft-pulse-dot"
              style={{ background: green, boxShadow: `0 0 12px ${green}` }}
            />
            <span className="relative inline-flex h-3 w-3 rounded-full" style={{ background: green }} />
          </span>
          <div className="flex flex-col leading-tight">
            <span className={`${isMobile ? "text-[15px]" : "text-[17px]"} font-[900] tracking-[-0.3px] text-foreground`}>
              SEO Engine Active
            </span>
            <span className="mt-[3px] text-[12.5px] font-[600]" style={{ color: textMuted }}>
              {seoLabelUrl
                ? seoLabelUrl.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "")
                : "Loading…"}
            </span>
          </div>
        </div>
        {/* MIDDLE — animated EKG pulse line (desktop only) */}
        {!isMobile && <PulseLineAnimation />}

        {/* RIGHT — Live · Updating pill (green) */}
        {!isMobile && (
          <div
            className="relative z-10 flex items-center gap-[7px] rounded-[20px] text-[12px] font-[800]"
            style={{
              color: green,
              background: greenSoft,
              border: `1px solid ${greenBorder}`,
              padding: "8px 14px",
            }}
          >
            <span
              className="inline-block h-[7px] w-[7px] rounded-full soft-pulse-dot"
              style={{ background: green, boxShadow: `0 0 8px ${green}` }}
            />
            Live · Updating
          </div>
         )}
       </div>

      <GscPremiumCard
        gscIsConnected={gscIsConnected}
        gscPropertySelected={gscPropertySelected}
        gscProperty={gscProperty}
        gscLoading={gscLoading}
        gscError={gscError}
        gscData={gscData}
        gscDateRange={gscDateRange}
        gscConnectedAt={gscConnectedAt}
        onConnect={handleConnectGsc}
        onDisconnect={handleDisconnectGsc}
        onChangeProperty={handleChangeProperty}
        onSelectProperty={() => navigate("/gsc-select-property")}
        onDateRangeChange={handleDateRangeChange}
      />

       <PerformancePanel
        isMobile={isMobile}
        impressions={gscData ? gscData.impressions : null}
        clicks={gscData ? gscData.clicks : null}
        gscConnected={gscIsConnected && gscPropertySelected}
        top3={top3Count}
        top10={top10Count}
        top1120={top1120Count}
        totalRanked={totalRankedKeywords}
        visibilityScore={healthScore}
        pagesThisWeek={pagesThisWeek}
        totalPages={totalPages}
        posts={posts}
      />

      <RankingEngine />



      {(() => {
        // ───── Premium dark style tokens (matches reference) ─────
        const GOLD = "#F59E0B";
        const GREEN = "#00FF88";
        const AMBER = "#F59E0B";
        const RED = "#EF4444";
        const PURPLE = "#8B5CF6";
        const tierMeta = (tier: number) => {
          if (tier === 1) return { color: GOLD, label: "Top 3" };
          if (tier === 2) return { color: GREEN, label: "Top 10" };
          if (tier === 3) return { color: AMBER, label: "11–20" };
          return { color: RED, label: "20+" };
        };
        const premiumCard: React.CSSProperties = {
          position: "relative",
          background: "linear-gradient(180deg, rgba(10,15,30,0.96) 0%, rgba(8,12,24,0.96) 100%)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 20,
          padding: isMobile ? 14 : 24,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
          overflow: "hidden",
          color: "#fff",
        };
        const livePill = (
          <span
            className="inline-flex items-center gap-[6px] rounded-full text-[10px] font-[700]"
            style={{
              color: GREEN,
              background: "rgba(0,255,136,0.08)",
              border: "1px solid rgba(0,255,136,0.25)",
              padding: "5px 11px",
              boxShadow: "0 0 16px rgba(0,255,136,0.18)",
            }}
          >
            <span
              className="inline-block h-[6px] w-[6px] rounded-full"
              style={{ background: GREEN, boxShadow: `0 0 8px ${GREEN}`, animation: "pulseRing 1.6s infinite" }}
            />
            Live
          </span>
        );

        // Filter the ranking list
        const filtered = rankingSetupKeywords.filter((k) => !k.pending && (tierFilter === 0 || k.tier === tierFilter));
        const counts = {
          0: rankingSetupKeywords.filter((k) => !k.pending).length,
          1: rankingSetupKeywords.filter((k) => k.tier === 1).length,
          2: rankingSetupKeywords.filter((k) => k.tier === 2).length,
          3: rankingSetupKeywords.filter((k) => k.tier === 3).length,
          4: rankingSetupKeywords.filter((k) => k.tier === 4).length,
        } as Record<0 | 1 | 2 | 3 | 4, number>;

        const tabs: Array<{ tier: 0 | 1 | 2 | 3 | 4; label: string; color: string; icon?: string }> = [
          { tier: 0, label: "All", color: "#FFFFFF" },
          { tier: 1, label: "Top 3", color: GOLD, icon: "★" },
          { tier: 2, label: "Top 10", color: GREEN },
          { tier: 3, label: "11–20", color: AMBER },
          { tier: 4, label: "20+", color: RED },
        ];

        // Map task category to premium type
        const taskTypeFor = (t: any): { label: string; color: string; bg: string; icon: JSX.Element; isArticle: boolean } => {
          const isArticle = t.category === "article";
          const isIndexing = t.category === "indexing";
          const isAi = t.category === "ai";
          if (isArticle) {
            return {
              label: "ARTICLE",
              color: GOLD,
              bg: "rgba(245,158,11,0.12)",
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              ),
              isArticle: true,
            };
          }
          if (isIndexing) {
            return {
              label: "INDEXING",
              color: "#06B6D4",
              bg: "rgba(6,182,212,0.12)",
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14a9 3 0 0 0 18 0V5" /><path d="M3 12a9 3 0 0 0 18 0" />
                </svg>
              ),
              isArticle: false,
            };
          }
          if (isAi) {
            return {
              label: "AI",
              color: PURPLE,
              bg: "rgba(139,92,246,0.14)",
              icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" /><path d="M5 21v-2a7 7 0 0 1 14 0v2" />
                </svg>
              ),
              isArticle: false,
            };
          }
          // keyword / rank / backlink / speed / other → KEYWORD purple
          return {
            label: t.category === "rank" ? "RANK" : t.category === "backlink" ? "BACKLINK" : t.category === "speed" ? "SPEED" : "KEYWORD",
            color: PURPLE,
            bg: "rgba(139,92,246,0.14)",
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={PURPLE} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" />
              </svg>
            ),
            isArticle: false,
          };
        };

        const visibleTasks = liveTasks.slice(0, 6);

        return (
          <div className={`mb-[18px] grid gap-[16px] ${isMobile ? "grid-cols-1" : "grid-cols-2"}`}>
            {/* ──────────────── RANKING PROGRESS ──────────────── */}
            <div data-tour-id="ranking-progress" style={{ ...premiumCard, display: "flex", flexDirection: "column", height: "100%" }}>
              {/* ambient glow */}
              <div
                className="pointer-events-none absolute -left-20 -top-20 h-[260px] w-[260px] rounded-full"
                style={{ background: `radial-gradient(circle, ${GOLD}22 0%, transparent 70%)` }}
              />
              <div className="relative z-10 mb-[18px] flex items-start justify-between">
                <div>
                  <div className="text-[22px] font-[800] tracking-tight" style={{ color: "#fff" }}>Ranking Progress</div>
                  <div className="mt-[3px] text-[12px] font-[500]" style={{ color: "rgba(255,255,255,0.45)" }}>
                    Your performance board · {rankingSetupKeywords.length} keyword{rankingSetupKeywords.length === 1 ? "" : "s"}
                  </div>
                </div>
                {livePill}
              </div>

              {/* Filter tabs */}
              <div className="relative z-10 mb-[14px] flex gap-[8px] overflow-x-auto sm:flex-wrap -mx-1 px-1 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
                {tabs.map((t) => {
                  const active = tierFilter === t.tier;
                  return (
                    <button
                      key={t.tier}
                      type="button"
                      onClick={() => setTierFilter(t.tier)}
                      className="shrink-0 whitespace-nowrap rounded-full text-[11.5px] font-[800] transition-all duration-200"
                      style={{
                        padding: "7px 14px",
                        color: active ? t.color : "rgba(255,255,255,0.5)",
                        background: active ? `${t.color}1F` : "rgba(255,255,255,0.03)",
                        border: `1px solid ${active ? `${t.color}66` : "rgba(255,255,255,0.06)"}`,
                        boxShadow: active ? `0 0 18px ${t.color}33` : "none",
                      }}
                    >
                      {t.icon ? <span className="mr-1">{t.icon}</span> : null}
                      {t.label}
                      {counts[t.tier] > 0 && (
                        <span className="ml-[6px] opacity-70">{counts[t.tier]}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="relative z-10 flex-1 flex flex-col min-h-0">
                <div className="flex-1 min-h-0 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
                  {filtered.length > 0 ? (
                    <div className="flex flex-col gap-[8px]">
                      {filtered.map((k, idx) => {
                        const { color } = tierMeta(k.tier);
                        const isTop3 = k.tier === 1;
                        const delta = k.delta;
                        return (
                          <div
                            key={k.id}
                            className="relative flex items-center justify-between gap-[10px] rounded-[12px] animate-fade-in"
                            style={{
                              padding: "12px 14px 12px 16px",
                              background: isTop3
                                ? `linear-gradient(90deg, ${color}14 0%, ${color}06 60%, transparent 100%)`
                                : "rgba(255,255,255,0.02)",
                              border: `1px solid ${color}33`,
                              borderLeft: `3px solid ${color}`,
                              boxShadow: isTop3 ? `0 0 24px ${color}1A inset` : "none",
                            }}
                          >
                            <div className="flex min-w-0 items-center gap-[10px]">
                              {isTop3 && (
                                <span style={{ color, fontSize: 16, filter: `drop-shadow(0 0 6px ${color}88)` }}>★</span>
                              )}
                              <span className="truncate text-[13.5px] font-[700]" style={{ color: "#fff" }}>
                                {k.keyword}
                              </span>
                            </div>
                            <div className="flex shrink-0 items-center gap-[10px]">
                              {delta != null && (
                                delta > 0 ? (
                                  <span className="text-[11px] font-[800]" style={{ color: GREEN }}>▲ {delta}</span>
                                ) : delta < 0 ? (
                                  <span className="text-[11px] font-[800]" style={{ color: RED }}>▼ {Math.abs(delta)}</span>
                                ) : (
                                  <span className="text-[11px] font-[700]" style={{ color: "rgba(255,255,255,0.4)" }}>— 0</span>
                                )
                              )}
                              <div className="relative">
                                {isTop3 && (
                                  <span
                                    className="absolute left-1/2 -translate-x-1/2"
                                    style={{ top: -14, fontSize: 14, filter: `drop-shadow(0 0 6px ${color}AA)` }}
                                  >
                                    👑
                                  </span>
                                )}
                                <span
                                  className="inline-flex items-center justify-center rounded-[10px] text-[14px] font-[800]"
                                  style={{
                                    color,
                                    background: `${color}14`,
                                    border: `1.5px solid ${color}66`,
                                    padding: "5px 12px",
                                    minWidth: 56,
                                    boxShadow: `0 0 14px ${color}33`,
                                  }}
                                >
                                  #{k.rank}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[200px] items-center justify-center text-[13px]" style={{ color: "rgba(255,255,255,0.4)" }}>
                      No keywords in this tier yet
                    </div>
                  )}
                </div>

                {/* bottom green wave + link */}
                <div className="relative mt-[16px] pt-[12px] shrink-0">
                  <svg
                    className="pointer-events-none absolute inset-x-0 -top-2 h-[24px] w-full opacity-60"
                    viewBox="0 0 400 24" preserveAspectRatio="none"
                  >
                    <defs>
                      <linearGradient id="rp-wave" x1="0" x2="1">
                        <stop offset="0%" stopColor={GREEN} stopOpacity="0" />
                        <stop offset="50%" stopColor={GREEN} stopOpacity="0.7" />
                        <stop offset="100%" stopColor={GREEN} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0 12 Q 50 4 100 12 T 200 12 T 300 12 T 400 12" fill="none" stroke="url(#rp-wave)" strokeWidth="1.5">
                      <animate attributeName="d"
                        values="M0 12 Q 50 4 100 12 T 200 12 T 300 12 T 400 12;
                                M0 12 Q 50 20 100 12 T 200 12 T 300 12 T 400 12;
                                M0 12 Q 50 4 100 12 T 200 12 T 300 12 T 400 12"
                        dur="4s" repeatCount="indefinite" />
                    </path>
                  </svg>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent("orbit:navigate-tab", { detail: "rankings" }))}
                    className="block w-full text-center text-[12px] font-[700] transition-opacity hover:opacity-80"
                    style={{ color: GREEN }}
                  >
                    {rankingSetupKeywords.length > filtered.length
                      ? `+ ${rankingSetupKeywords.length - filtered.length} more keywords — view full rankings →`
                      : "view full rankings →"}
                  </button>
                </div>
              </div>
            </div>

            {/* ──────────────── LIVE TASKS ──────────────── */}
            <div data-tour-id="live-tasks" style={premiumCard}>
              <div
                className="pointer-events-none absolute -right-20 -top-20 h-[260px] w-[260px] rounded-full"
                style={{ background: `radial-gradient(circle, ${PURPLE}22 0%, transparent 70%)` }}
              />
              <div className="relative z-10 mb-[18px] flex items-start justify-between">
                <div>
                  <div className="text-[22px] font-[800] tracking-tight" style={{ color: "#fff" }}>Live Tasks</div>
                  <div className="mt-[3px] text-[12px] font-[500]" style={{ color: "rgba(255,255,255,0.45)" }}>
                    Engine activity feed
                  </div>
                </div>
                {livePill}
              </div>

              {visibleTasks.length > 0 ? (
                <div className="relative z-10">
                  {/* timeline vertical line */}
                  <div
                    className="absolute left-[18px] top-[8px] bottom-[8px] w-px"
                    style={{ background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.08), transparent)" }}
                  />
                  <div className="flex flex-col gap-[10px]">
                    {visibleTasks.map((t) => {
                      const tt = taskTypeFor(t);
                      const isDone = t.status === "completed";
                      const isFailed = t.status === "failed";
                      const isRunning = t.status === "running" || t.status === "recovering";
                      const isPriority = t.priority >= 4;
                      return (
                        <div key={t.id} className="relative flex items-stretch gap-[14px] pl-[6px] sm:pl-[10px] animate-fade-in">
                          {/* timeline dot */}
                          <div
                            className="absolute left-[10px] sm:left-[14px] top-[22px] h-[10px] w-[10px] rounded-full"
                            style={{ background: tt.color, boxShadow: `0 0 12px ${tt.color}AA`, border: "2px solid #0A0F1E" }}
                          />
                          <div
                            className="ml-[14px] sm:ml-[18px] flex flex-1 min-w-0 items-center gap-[10px] sm:gap-[14px] rounded-[14px]"
                            style={{
                              padding: isMobile ? "10px 10px" : "14px 14px",
                              background: "rgba(255,255,255,0.02)",
                              border: `1px solid ${tt.color}26`,
                              boxShadow: isPriority ? `inset 0 0 0 1px ${tt.color}1A` : "none",
                            }}
                          >
                            {/* icon tile */}
                            <div className="relative shrink-0">
                              <div
                                className="flex h-[44px] w-[44px] items-center justify-center rounded-[12px]"
                                style={{ background: tt.bg, border: `1px solid ${tt.color}33` }}
                              >
                                {tt.icon}
                              </div>
                              {tt.isArticle && (
                                <span
                                  className="absolute -right-1 -top-1 flex h-[16px] w-[16px] items-center justify-center rounded-full text-[10px]"
                                  style={{ background: GOLD, color: "#000", boxShadow: `0 0 8px ${GOLD}AA` }}
                                >
                                  ★
                                </span>
                              )}
                            </div>

                            {/* content */}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-[8px]">
                                <span
                                  className="text-[10px] font-[800] tracking-[1.2px]"
                                  style={{ color: tt.color }}
                                >
                                  {tt.label}
                                </span>
                                {isPriority && tt.isArticle && (
                                  <>
                                    <span style={{ color: "rgba(255,255,255,0.25)" }}>·</span>
                                    <span className="text-[10px] font-[800] tracking-[1.2px]" style={{ color: PURPLE }}>
                                      PRIORITY
                                    </span>
                                  </>
                                )}
                              </div>
                              <div className="mt-[4px] truncate text-[14px] font-[700]" style={{ color: "#fff" }} title={t.name}>
                                {t.name}
                              </div>
                              <div className="mt-[6px] flex items-center gap-[10px]">
                                <span
                                  className="inline-flex items-center gap-[5px] rounded-full text-[10.5px] font-[700]"
                                  style={{
                                    color: isFailed ? RED : GREEN,
                                    background: isFailed ? "rgba(239,68,68,0.10)" : "rgba(0,255,136,0.08)",
                                    border: `1px solid ${isFailed ? "rgba(239,68,68,0.3)" : "rgba(0,255,136,0.25)"}`,
                                    padding: "3px 9px",
                                  }}
                                >
                                  {isRunning && (
                                    <span
                                      className="inline-block h-[5px] w-[5px] rounded-full"
                                      style={{ background: GREEN, animation: "pulseRing 1.4s infinite" }}
                                    />
                                  )}
                                  {isDone && <span style={{ color: GREEN }}>✓</span>}
                                  {isFailed && <span>✕</span>}
                                  {isRunning ? "Running" : isDone ? "Done" : isFailed ? "Failed" : "Done"}
                                </span>
                                <span className="text-[10.5px] font-[600]" style={{ color: "rgba(255,255,255,0.4)" }}>
                                  {t.time}
                                </span>
                              </div>
                            </div>

                            {/* right indicator */}
                            <div className="shrink-0">
                              {isDone ? (
                                <div
                                  className="flex h-[36px] w-[36px] items-center justify-center rounded-full"
                                  style={{
                                    background: "rgba(0,255,136,0.12)",
                                    border: `1.5px solid ${GREEN}`,
                                    boxShadow: `0 0 14px ${GREEN}55`,
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                </div>
                              ) : isRunning ? (
                                // radar
                                <div className="relative h-[36px] w-[36px]">
                                  {[0, 1, 2].map((i) => (
                                    <span
                                      key={i}
                                      className="absolute inset-0 rounded-full"
                                      style={{
                                        border: `1px solid ${GREEN}`,
                                        opacity: 0.5,
                                        animation: `pulseRing 2s ${i * 0.6}s infinite`,
                                      }}
                                    />
                                  ))}
                                  <span
                                    className="absolute left-1/2 top-1/2 h-[6px] w-[6px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                                    style={{ background: GREEN, boxShadow: `0 0 10px ${GREEN}` }}
                                  />
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* footer bar */}
                  <div className="mt-[16px] flex items-center justify-between border-t pt-[12px]" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new CustomEvent("orbit:navigate-tab", { detail: "rankings" }))}
                      className="text-[11.5px] font-[700] transition-opacity hover:opacity-80"
                      style={{ color: GREEN }}
                    >
                      View all activity →
                    </button>
                    <span className="flex items-center gap-[6px] text-[10.5px] font-[700]" style={{ color: GREEN }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="7 17 17 7" /><polyline points="7 7 17 7 17 17" />
                      </svg>
                      Auto-updating
                      <span
                        className="inline-block h-[6px] w-[6px] rounded-full"
                        style={{ background: GREEN, boxShadow: `0 0 8px ${GREEN}`, animation: "pulseRing 1.6s infinite" }}
                      />
                    </span>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-[13px]" style={{ color: "rgba(255,255,255,0.4)" }}>
                  No tasks running yet
                </div>
              )}
            </div>
          </div>
        );
      })()}


      <AiRadarCard />

      <div
        className="mb-[18px]"
        style={{
          background: "linear-gradient(145deg, hsl(var(--card)) 0%, hsl(var(--secondary)) 100%)",
          border: `1px solid ${greenBorder}`,
          borderRadius: 16,
          padding: isMobile ? 16 : 22,
          boxShadow: "0 0 40px hsl(var(--primary) / 0.04)",
        }}
      >
        <div className="mb-1 flex items-center justify-between">
          <div className="text-[13.5px] font-[800]">🧠 AI Decisions</div>
          <span className="text-[9.5px] font-[700] text-primary">Intelligence Feed</span>
        </div>
        {aiDecisions.length > 0 ? (
          <div className={`mt-1 grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-x-6`}>
            {aiDecisions.map((d, i) => (
              <div
                key={i}
                className="flex items-start gap-[11px] py-[11px]"
                style={{ borderBottom: "1px solid hsl(var(--border))" }}
              >
                <div
                  className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] text-[13px]"
                  style={{ background: greenDim, border: `1px solid ${greenBorder}` }}
                >
                  {d.icon}
                </div>
                <div>
                  <div className="text-[12px] font-[500] leading-[1.45]">{d.text}</div>
                  <div className="mt-[3px] text-[9.5px]" style={{ color: textDim }}>{d.time}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-6 text-center text-[12.5px]" style={{ color: textMuted }}>
            AI is analyzing your website. Check back soon.
          </div>
        )}
       </div>

       <AiInsightPreview />

      <DashboardBottomSection clientId={ownClientId} />

    </div>
    </TooltipProvider>
  );
};

export default DashboardHome;
